import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const AU_START_YEAR = 2014; // D32 Australia founding — journey text auto-updates each Feb
function journeyYears() {
  const now = new Date();
  const y = now.getFullYear() - AU_START_YEAR + (now.getMonth() >= 1 ? 0 : -1);
  return y;
}

const VISION = [
  {
    title: "100 regions by 2030",
    body:
      "From Chandigarh Tricity, Ahmedabad and Delhi-NCR today, to every major Indian metro and tier-2 city over the next five years.",
  },
  {
    title: "10,000 members strong",
    body:
      "A community of vetted, give-first business owners doing serious business together across India.",
  },
  {
    title: "The Indian SME's board of advisors",
    body:
      "Every member gets a peer table, a mentor pool, and a referral pipeline that compounds year on year.",
  },
  {
    title: "Growth without gate-keeping",
    body:
      "Open the room to ambitious business owners who typically get locked out of legacy networks.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "D32 India isn't a networking club. It's the peer table I wish I had when I was starting out. Every meeting moves my business forward.",
    name: "Simran Kaur",
    role: "Partner, Kaur & Co. Chartered Accountants · Chandigarh Tricity",
  },
  {
    quote:
      "In six months I've closed three high-value clients from D32 introductions. But the deeper win is the mentors I now WhatsApp on tough days.",
    name: "Karan Shah",
    role: "MD, Shah Logistics Express · Ahmedabad",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-white">
      {/* HERO */}
      <section className="bg-navy text-white py-24 lg:py-32">
        <div className="container-narrow">
          <div className="overline text-gold mb-4">About D32 India</div>
          <h1 className="text-4xl sm:text-6xl font-display font-semibold leading-[1.05] max-w-4xl">
            A {journeyYears()}-year journey across Australia. Now home in{" "}
            <span className="italic text-gold">India</span>.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-gray-300 leading-relaxed">
            Inspired by D32's proven Australian playbook, we've brought the
            model home, re-engineered for the Indian SME, Indian network,
            Indian rhythm.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 lg:py-32">
        <div className="container-narrow grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-gold mb-3">The Story</div>
            <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy">
              Too many great businesses, struggling alone.
            </h2>
          </div>
          <div className="lg:col-span-7 text-gray-700 space-y-5 leading-relaxed">
            <p>
              We've seen this pattern too many times. A founder with a great
              product, real ambition, and a fierce work ethic, quietly losing
              momentum because they don't have the connections, mentors and
              networks that big businesses take for granted.
            </p>
            <p>
              The result? Avoidable mistakes. Missed opportunities. Six
              months chasing the wrong client when the right introduction
              would have changed everything.
            </p>
            <p>
              D32 India fixes that. We get you in the right room, with the
              right people, and teach you how to win, together.
            </p>
          </div>
        </div>
      </section>

      {/* IMG SPLIT */}
      <section className="bg-cream py-24 lg:py-32">
        <div className="container-narrow grid lg:grid-cols-2 gap-12 items-center">
          <div
            className="aspect-[4/5] rounded-3xl bg-cover bg-center shadow-xl"
            style={{
              backgroundImage:
                "url(https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
            }}
          />
          <div>
            <div className="overline text-gold mb-3">Our Model</div>
            <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy">
              Give first. Grow forever.
            </h2>
            <p className="mt-5 text-gray-700 leading-relaxed">
              Most networking groups are transactional. Shove a card, hope
              for a lead. D32 flips that model on its head.
            </p>
            <p className="mt-4 text-gray-700 leading-relaxed">
              We onboard you properly. We teach you how to conduct a 1:1
              because trust is earned in this business community. We show
              you how to give a quality referral. And we hold you to a
              standard.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                ["6-12", "months to build full trust"],
                ["100s", "of founders in your network"],
                ["3", "tiers of growth"],
              ].map(([k, v]) => (
                <div key={k} className="border-t-2 border-gold pt-3">
                  <div className="font-display text-2xl text-navy">{k}</div>
                  <div className="text-xs text-gray-600 mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VISION FOR INDIA */}
      <section className="py-24 lg:py-28 bg-white">
        <div className="container-narrow grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline text-gold mb-3">Our Vision for India</div>
            <h2 className="text-3xl sm:text-4xl font-display font-semibold text-navy leading-tight">
              Build the largest peer-led growth network for Indian business owners.
            </h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {VISION.map((v) => (
              <div key={v.title} className="bg-cream rounded-2xl p-6 border border-gray-200">
                <h3 className="font-display text-lg text-navy">{v.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy text-white py-24 lg:py-28">
        <div className="container-narrow">
          <div className="overline text-gold mb-4">Voices from the room</div>
          <h2 className="text-3xl sm:text-4xl font-display font-semibold leading-tight max-w-2xl">
            Members. In their own words.
          </h2>
          <div className="mt-14 grid md:grid-cols-2 gap-8">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.name}
                className="border border-white/15 rounded-2xl p-8 hover:border-gold/60 transition-colors bg-white/[0.02]"
              >
                <div className="text-gold text-3xl font-display leading-none">"</div>
                <p className="mt-2 text-gray-200 leading-relaxed">{t.quote}</p>
                <div className="mt-6 text-sm">
                  <div className="font-semibold text-white">{t.name}</div>
                  <div className="text-gray-400">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-narrow">
          <div className="bg-navy text-white rounded-3xl p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-xl">
              <h3 className="text-3xl sm:text-4xl font-display font-semibold leading-tight">
                Ready to join the network?
              </h3>
              <p className="mt-3 text-gray-300">
                We'll host you at the next Chandigarh Tricity, Ahmedabad or Delhi-NCR meeting.
              </p>
            </div>
            <Link
              to="/visit-a-circle"
              className="inline-flex items-center gap-2 bg-gold text-white font-semibold px-7 py-4 rounded-full hover:-translate-y-0.5 transition-all"
            >
              Visit a Circle <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
